import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createCn } from '../../helpers/utils'
import classes from './styles.module.scss'
import { useTranslation } from 'react-i18next'
import { BellIcon } from '../../assets'
import AddSpeakerPanel from './components/add-speaker-panel.component'
import SpeakerGrid from './components/speaker-grid.component'
import { apiService } from '../../services/api.service'
import { mapSpeakerFromApi } from './utils/map-speaker'

const cn = createCn('speakers', classes)

export type Speaker = {
  id: string;
  name: string;
  position: string;
  date: string;
  voice: File | null;
  duration: string;
  hasVoice: boolean;
  file?: File | null;
}

const SpeakersPage = () => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [list, setList] = useState<Speaker[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [highlightPanel, setHighlightPanel] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const addPanelRef = useRef<HTMLDivElement>(null);

  const loadSpeakers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getSpeakers();
      setList(data.map(mapSpeakerFromApi));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('speakers.load-error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadSpeakers();
  }, [loadSpeakers]);

  const filteredList = useMemo(() => {
    return list
      .filter(item => {
        const q = query.toLowerCase();
        return (
          item.name.toLowerCase().includes(q)
          || item.position.toLowerCase().includes(q)
        );
      })
      .sort((a, b) => {
        const [dayA, monthA, yearA] = a.date.split('.').map(Number);
        const dateA = new Date(yearA, monthA - 1, dayA);
        const [dayB, monthB, yearB] = b.date.split('.').map(Number);
        const dateB = new Date(yearB, monthB - 1, dayB);
        return dateB.getTime() - dateA.getTime();
      });
  }, [list, query]);

  useEffect(() => {
    if (showSearch) searchRef.current?.focus();
  }, [showSearch]);

  const scrollToAddPanel = () => {
    addPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setHighlightPanel(true);
    window.setTimeout(() => setHighlightPanel(false), 2000);
    window.setTimeout(() => {
      addPanelRef.current?.querySelector<HTMLInputElement>('#speaker-name')?.focus();
    }, 350);
  };

  const handleAddClick = () => {
    scrollToAddPanel();
  };

  return (
    <div className={cn()}>
      <header className={cn('topbar')}>
        <h1 className={cn('title')}>{t('speakers.heading')}</h1>
        <div className={cn('topbar-actions')}>
          {showSearch && (
            <input
              ref={searchRef}
              type="text"
              className={cn('search-input')}
              placeholder={t('speakers.search-placeholder')}
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          )}
          <button
            type="button"
            className={cn('icon-btn')}
            aria-label={t('speakers.search-placeholder')}
            onClick={() => setShowSearch(prev => !prev)}
          >
            <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden>
              <circle cx="8" cy="8" r="5.2" stroke="currentColor" strokeWidth="1.5" fill="none" />
              <line x1="11.5" y1="11.5" x2="17" y2="17" stroke="currentColor" strokeWidth="1.5" />
            </svg>
          </button>
          <button type="button" className={cn('icon-btn')} aria-label="Settings">
            <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden>
              <path d="M3 6h14M3 10h14M3 14h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
          <button type="button" className={cn('icon-btn')} aria-label="Notifications">
            <BellIcon />
          </button>
        </div>
      </header>

      <div ref={addPanelRef}>
        <AddSpeakerPanel onAdded={loadSpeakers} highlight={highlightPanel} />
      </div>

      {loading && <p className={cn('status')}>{t('speakers.loading')}</p>}
      {error && (
        <p className={cn('status', { error: true })}>
          {error}
          <button type="button" onClick={loadSpeakers}>{t('action.retry')}</button>
        </p>
      )}

      {!loading && !error && (
        <SpeakerGrid
          items={filteredList}
          onRefresh={loadSpeakers}
          onAddClick={handleAddClick}
          activeId={activeId}
          onActivate={setActiveId}
          onDeactivate={() => setActiveId(null)}
        />
      )}

    </div>
  );
}

export default SpeakersPage
